import { useQuery } from '@tanstack/react-query';
import type { AuthUser } from '../../stores/authStore';
import type { AuthSessionPayload } from '../types';
import { appendQuery, fetchJson } from '../lib/api';

interface AuthConfigPayload {
  authBaseUrl: string | null;
  passkey?: boolean;
  providers?: { github?: boolean; google?: boolean; microsoft?: boolean };
}

interface HostedSiteWidgetPayload {
  organizationId: string;
  organizationSlug: string;
  projectId: string;
  projectSlug: string;
  apiKey: string;
  apiBaseUrl: string;
  browserScriptUrl: string;
  browserScriptIntegrity?: string | null;
  widgetScriptUrl?: string | null;
  widgetScriptIntegrity?: string | null;
  widgetStyleUrl?: string | null;
  widgetStyleIntegrity?: string | null;
  recorderScriptUrl?: string | null;
  recorderScriptIntegrity?: string | null;
  releaseManifestUrl?: string | null;
  bootstrapOrigin?: string | null;
}

interface SessionState {
  user: AuthUser;
  session: {
    id: string;
    token: string;
  };
  bearerToken: string | null;
}

function resolveAuthBaseUrl(value: string | null | undefined): string | null {
  if (typeof window === 'undefined') {
    return value ?? null;
  }

  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') {
    return `${window.location.origin}/api/auth/ba`;
  }

  return value ?? null;
}

async function fetchSessionState(authBaseUrl: string): Promise<SessionState | null> {
  const response = await fetch(`${authBaseUrl}/get-session`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Unable to restore session (${response.status}).`);
  }

  const payload = (await response.json()) as
    | {
        session?: { id: string; token: string };
        user?: AuthUser;
      }
    | null;

  if (!payload?.session || !payload.user) {
    return null;
  }

  return {
    session: payload.session,
    user: payload.user,
    bearerToken: payload.session.token ?? null,
  };
}

export function useAuthConfig(enabled = true) {
  return useQuery({
    queryKey: ['auth-config'],
    queryFn: () =>
      fetchJson<{ success: true } & AuthConfigPayload>('/api/auth/config').then((payload) => ({
        ...payload,
        authBaseUrl: resolveAuthBaseUrl(payload.authBaseUrl),
      })),
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}

export function useAuthSession(token: string | null, enabled = true) {
  return useQuery({
    queryKey: ['auth-session', token],
    queryFn: () =>
      fetchJson<{ success: true; session: AuthSessionPayload }>('/api/auth/me', token).then(
        (payload) => payload.session
      ),
    enabled: Boolean(token) && enabled,
    retry: false,
  });
}

export function useSessionState(authBaseUrl: string, scope: string, enabled = true) {
  return useQuery({
    queryKey: ['auth-session-state', authBaseUrl, scope],
    queryFn: () => fetchSessionState(authBaseUrl),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

export function useHostedSiteWidget(enabled = true) {
  return useQuery({
    queryKey: ['hosted-site-widget'],
    queryFn: () =>
      fetchJson<{ success: true } & HostedSiteWidgetPayload>(
        appendQuery('/api/public/site-widget', {
          siteOrigin: typeof window === 'undefined' ? undefined : window.location.origin,
        })
      ),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}
